import { bindable, bindingMode, customElement, autoinject, observable } from "aurelia-framework";
import {
    validateTrigger,
    ValidationController,
    ValidationControllerFactory,
    ValidationRules
} from "aurelia-validation";

import nameof from "../../../../common/nameof";
import { IAncestorSettings, IInputOptions } from "../../../../interfaces/form-builder/i-input-options";
import { NoteDataManager } from "../../note-data-manager";
import { FormValidationEnum } from "../../../../enums/form-validation-enum";
import { IListGroupTimeOverlap, IValidation } from "../../../../interfaces/form-builder/i-validation";

@customElement("time-input")
@autoinject
export class TimeInput {
    @bindable({ defaultBindingMode: bindingMode.twoWay })
    @observable({ changeHandler: nameof<TimeInput>("resultChanged") })
    public result: any;
    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public viewOptions: IInputOptions;
    @bindable({ defaultBindingMode: bindingMode.toView })
    public ancestorSettings: IAncestorSettings;
    public noteDataManager: NoteDataManager;
    public validationListGroupOverlap: IListGroupTimeOverlap = null;
    public noFutureTimeValidation: IValidation = null;
    public timeRanges: any[] = [];
    public validationController: ValidationController;

    public constructor(noteDataManager: NoteDataManager, controllerFactory: ValidationControllerFactory) {
        this.noteDataManager = noteDataManager;
        this.validationController = controllerFactory.createForCurrentScope();
        this.validationController.validateTrigger = validateTrigger.changeOrBlur;
    }

    public attached() {
        let elementNameSplit = this.viewOptions.name?.split(".");
        let isListGroup = elementNameSplit.length > 2;
        if (isListGroup) {
            this.extractListGroupValidations(elementNameSplit[0]);
        }
        this.initValidations();
    }

    private extractListGroupValidations(listGroupName: string) {
        let validations = this.noteDataManager.getValidationFromProp(listGroupName);
        this.validationListGroupOverlap = validations?.find(
            (validation) => validation.validationType == FormValidationEnum.ListGroupTimeOverLap
        ) as IListGroupTimeOverlap;
    }

    private initValidations() {
        let validations = this.noteDataManager.getValidationFromProp(this.viewOptions.name);
        ValidationRules.customRule(
            "noOverlap",
            (value) => {
                if (!value) {
                    return true;
                } else {
                    return !this.checkForOverlap();
                }
            },
            `${this.validationListGroupOverlap?.message}`
        );
        let validationRules = ValidationRules.ensure((x: any) => x.result);
        validations?.forEach((rule: IListGroupTimeOverlap | IValidation) => {
            if (rule.validationType === FormValidationEnum.Required) {
                validationRules.required().withMessage(rule.message).on(this);
            }
            // If needed in the future, validation type should be updated to not conflict with encounter validaton in GIP.
            // if (rule.validationType === FormValidationEnum.DateIsNotInFuture) {
            //     validationRules
            //         .satisfies((value) => !rule || this.timeToMinutes(value) <= this.getCurrentTimeInMinutes())
            //         .withMessage(rule.message)
            //         .on(this);
            // }
        });
        if (!!this.validationListGroupOverlap) {
            validationRules.satisfiesRule("noOverlap", this.timeRanges).on(this);
        }
    }

    private getCurrentTimeInMinutes() {
        const now = new Date();
        return now.getHours() * 60 + now.getMinutes();
    }

    public resultChanged(newValue: string, oldValue: string) {
        if (!newValue || !oldValue || newValue == oldValue) {
            return;
        }
        if (!!this.validationListGroupOverlap) {
            // Grab existing time ranges from noteModel and format into list.
            let noteModel = this.noteDataManager.getNoteModel();
            let minTarget = this.validationListGroupOverlap.minTargetTimeName;
            let maxTarget = this.validationListGroupOverlap.maxTargetTimeName;
            for (let key in noteModel) {
                if (key.includes(minTarget)) {
                    let keySplit = key.split(".");
                    let index = Number(keySplit[1]);
                    this.timeRanges[index] = {
                        [minTarget]: this.noteDataManager.getValue(`${keySplit[0]}.${index}.${minTarget}`),
                        [maxTarget]: this.noteDataManager.getValue(`${keySplit[0]}.${index}.${maxTarget}`)
                    };
                }
            }
            // Add new value to time list
            let elementNameSplit = this.viewOptions.name?.split(".");
            let newIndex = Number(elementNameSplit[1]);
            let newTimeRange = {
                [minTarget]:
                    elementNameSplit[2] == minTarget
                        ? newValue
                        : this.noteDataManager.getValue(`${elementNameSplit[0]}.${newIndex}.${minTarget}`),
                [maxTarget]:
                    elementNameSplit[2] == maxTarget
                        ? newValue
                        : this.noteDataManager.getValue(`${elementNameSplit[0]}.${newIndex}.${maxTarget}`)
            };
            this.timeRanges[newIndex] = newTimeRange;
            return;
        }
    }

    public checkForOverlap() {
        // Converting time values to minutes since midnight
        const formattedRanges = this.timeRanges.map((range) => ({
            start: this.timeToMinutes(range.startTime),
            end: this.timeToMinutes(range.endTime)
        }));
        // Sort ranges by start time
        formattedRanges.sort((a, b) => a.start - b.start);
        // Check if an endTime happens before a start time
        for (let i = 0; i < formattedRanges.length - 1; i++) {
            if (formattedRanges[i].end > formattedRanges[i + 1].start) {
                return true;
            }
        }
        return false;
    }

    public timeToMinutes(timeStr: string) {
        if (!timeStr) {
            return null;
        }
        const [time, meridian] = timeStr.split(" ");
        let [hours, minutes] = time.split(":").map(Number);
        if (meridian === "PM" && hours !== 12) {
            hours += 12;
        }
        if (meridian === "AM" && hours === 12) {
            hours = 0;
        }
        return hours * 60 + minutes;
    }
}

export interface ITimeRange {
    startTime: string;
    endTime: string;
}
